
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, ExternalLink } from "lucide-react";
import { InternalCandidate } from "../InternalCandidates";

interface InternalCandidatesTableProps {
  candidates: InternalCandidate[];
  loading: boolean;
  onEdit: (candidate: InternalCandidate) => void;
  onDelete: (candidate: InternalCandidate) => void;
}

export function InternalCandidatesTable({
  candidates,
  loading,
  onEdit,
  onDelete
}: InternalCandidatesTableProps) {
  if (loading) {
    return (
      <div className="bg-secondary/50 backdrop-blur-lg border border-primary/20 rounded-lg p-8">
        <div className="flex items-center justify-center">
          <div className="text-white">Loading internal candidates...</div>
        </div>
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div className="bg-secondary/50 backdrop-blur-lg border border-primary/20 rounded-lg p-8">
        <div className="text-center">
          <div className="text-white mb-2">No internal candidates found</div>
          <div className="text-primary-light text-sm">
            Add candidates manually or import from Excel to get started
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-secondary/50 backdrop-blur-lg border border-primary/20 rounded-lg shadow-lg shadow-primary/5">
      <div className="p-4">
        <div className="rounded-md border border-primary/20 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-primary/30 hover:bg-primary/30">
                <TableHead className="text-white text-[11px] text-center py-0 w-[60px]">SN</TableHead>
                <TableHead className="text-white text-[11px] text-left py-0 w-[120px]">Role</TableHead>
                <TableHead className="text-white text-[11px] text-left py-0 w-[100px]">Country</TableHead>
                <TableHead className="text-white text-[11px] text-left py-0 w-[100px]">City</TableHead>
                <TableHead className="text-white text-[11px] text-left py-0 w-[150px]">Name</TableHead>
                <TableHead className="text-white text-[11px] text-left py-0 w-[200px]">Skillset</TableHead>
                <TableHead className="text-white text-[11px] text-center py-0 w-[90px]">Experience (yrs)</TableHead>
                <TableHead className="text-white text-[11px] text-center py-0 w-[80px]">LinkedIn</TableHead>
                <TableHead className="text-white text-[11px] text-left py-0 w-[200px]">Past Projects</TableHead>
                <TableHead className="text-white text-[11px] text-center py-0 w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {candidates.map((candidate) => (
                <TableRow 
                  key={candidate.id}
                  className="hover:bg-primary/20 transition-colors bg-secondary-light/5"
                >
                  <TableCell className="text-white text-[11px] text-center py-0">
                    {candidate.sn}
                  </TableCell>
                  <TableCell className="text-white text-[11px] py-0">
                    {candidate.role}
                  </TableCell>
                  <TableCell className="text-white text-[11px] py-0">
                    {candidate.country}
                  </TableCell>
                  <TableCell className="text-white text-[11px] py-0">
                    {candidate.city}
                  </TableCell>
                  <TableCell className="text-white text-[11px] py-0 font-medium">
                    {candidate.candidate_name}
                  </TableCell>
                  <TableCell className="text-white text-[11px] py-0">
                    <div className="max-w-[200px] truncate" title={candidate.skillset}>
                      {candidate.skillset}
                    </div>
                  </TableCell>
                  <TableCell className="text-white text-[11px] text-center py-0">
                    {candidate.years_experience}
                  </TableCell>
                  <TableCell className="text-center py-0">
                    {candidate.linkedin_url && (
                      <a
                        href={candidate.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </TableCell>
                  <TableCell className="text-white text-[11px] py-0">
                    <div className="max-w-[200px] truncate" title={candidate.past_projects}>
                      {candidate.past_projects}
                    </div>
                  </TableCell>
                  <TableCell className="py-0">
                    <div className="flex items-center gap-1 justify-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(candidate)}
                        className="h-8 w-8 p-0 text-primary-light hover:text-primary hover:bg-primary/10"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(candidate)}
                        className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
